import UserAvatar from "../../../components/UserAvatar";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import "./style.css";

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 4,
  });

const AvailabilityBadge = ({ variant }) => {
  if (variant?.inventory_mode === "stocked") {
    const stock = Number(variant?.stock || 0);
    return (
      <div>
        <span className={`badge ${stock > 0 ? "bg-success-lt text-success" : "bg-danger-lt text-danger"}`}>
          {stock > 0 ? "Stok tersedia" : "Stok habis"}
        </span>
        <small className="d-block text-secondary mt-1">
          {formatQuantity(stock)} {variant?.unit || "pcs"}
        </small>
      </div>
    );
  }

  if (variant?.inventory_mode === "non_stock") {
    return <span className="badge bg-info-lt text-info">Selalu tersedia</span>;
  }

  const recipeReady = (variant?.materials || []).length > 0;
  const canSell = variant?.can_sell !== false && Number(variant?.available_qty || 0) > 0;

  return (
    <div>
      <span className={`badge ${canSell ? "bg-success-lt text-success" : "bg-warning-lt text-warning"}`}>
        {canSell ? "Siap dibuat" : recipeReady ? "Bahan belum cukup" : "Resep belum diatur"}
      </span>
      {recipeReady ? (
        <small className="d-block text-secondary mt-1">
          Kapasitas {formatQuantity(variant?.available_qty)} {variant?.unit || "pcs"}
        </small>
      ) : null}
    </div>
  );
};

const DetailProductGroup = ({ group, onClose, onOpenRecipe, onOpenProduction, canProcessProduction = false }) => {
  if (!group) return null;

  const variants = group?.variants || [];

  return (
    <div className="product-group-detail-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="product-group-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-group-detail-title"
        onMouseDown={(event) => event.stopPropagation()}>
        <header className="product-group-detail-header">
          <div className="d-flex align-items-center gap-3 min-w-0">
            <UserAvatar
              fullName={group?.name}
              imageUrl={group?.image_path || group?.image}
              image_path={group?.image_path || group?.image}
              size="lg"
              justPhotoView={false}
            />
            <div className="min-w-0">
              <div className="text-primary text-uppercase fw-bold fs-6 mb-1">Detail produk</div>
              <h3 className="mb-1 text-truncate" id="product-group-detail-title">
                {group?.name}
              </h3>
              <div className="d-flex flex-wrap gap-2 text-secondary small">
                <span>{group?.category_label || group?.category || "Tanpa kategori"}</span>
                <span>•</span>
                <span>{group?.type_label || group?.type || "Tanpa tipe"}</span>
                <span>•</span>
                <span>{variants.length} varian</span>
              </div>
            </div>
          </div>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Tutup detail produk" />
        </header>

        <div className="product-group-detail-summary">
          <div>
            <small>Nama pilihan</small>
            <strong>{group?.option_name || "Varian"}</strong>
          </div>
          <div>
            <small>Rentang harga</small>
            <strong>
              {formatIDR(group?.minimum_price || 0)}
              {Number(group?.minimum_price || 0) !== Number(group?.maximum_price || 0)
                ? ` – ${formatIDR(group?.maximum_price || 0)}`
                : ""}
            </strong>
          </div>
          <div>
            <small>Status katalog</small>
            <strong className={group?.can_sell ? "text-success" : "text-warning"}>
              {group?.can_sell ? "Siap dijual" : "Perlu dilengkapi"}
            </strong>
          </div>
        </div>

        <div className="product-group-detail-body">
          <div className="table-responsive product-group-variant-table-wrap">
            <table className="table table-vcenter mb-0 product-group-variant-table">
              <thead>
                <tr>
                  <th>Varian</th>
                  <th>SKU</th>
                  <th className="text-end">Harga jual</th>
                  <th className="text-end">HPP</th>
                  <th>Ketersediaan</th>
                  <th>Resep</th>
                  <th className="text-end">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((variant, index) => {
                  const materialCount = (variant?.materials || []).length;
                  const canProduce = variant?.inventory_mode === "stocked" && materialCount > 0;

                  return (
                    <tr key={variant?.id || `${group.id}-variant-${index}`}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="avatar avatar-sm bg-primary-lt text-primary fw-bold">{index + 1}</span>
                          <div>
                            <strong className="d-block">{variant?.variant_label || variant?.label || "Reguler"}</strong>
                            <small className="text-secondary">{variant?.unit || "pcs"}</small>
                            <small className="d-block text-secondary">
                              {variant?.inventory_mode === "stocked" ? "Stok siap jual" : variant?.inventory_mode === "non_stock" ? "Tanpa stok" : "Dibuat saat dipesan"}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        {variant?.sku ? (
                          <span className="badge bg-secondary-lt text-secondary">{variant.sku}</span>
                        ) : (
                          <span className="text-secondary">—</span>
                        )}
                      </td>
                      <td className="text-end fw-bold text-nowrap">{formatIDR(variant?.unit_price || 0)}</td>
                      <td className="text-end text-nowrap">{formatIDR(variant?.unit_cogs || 0)}</td>
                      <td>
                        <AvailabilityBadge variant={variant} />
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <span className={`badge ${materialCount ? "bg-primary-lt text-primary" : "bg-warning-lt text-warning"}`}>
                            {materialCount ? `${materialCount} bahan` : "Belum ada resep"}
                          </span>
                          {materialCount ? (
                            <small className="text-secondary product-group-material-preview">
                              {variant.materials
                                .slice(0, 2)
                                .map((material) => material?.material_name)
                                .filter(Boolean)
                                .join(", ")}
                              {materialCount > 2 ? ` +${materialCount - 2}` : ""}
                            </small>
                          ) : null}
                        </div>
                      </td>
                      <td className="text-end">
                        <div className="btn-list flex-nowrap justify-content-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#modal-form-recipe"
                            onClick={() => onOpenRecipe(group, variant)}>
                            {materialCount ? "Ubah resep" : "Atur resep"}
                          </button>
                          {variant?.inventory_mode === "stocked" && canProcessProduction ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              disabled={!canProduce}
                              data-bs-toggle={canProduce ? "modal" : undefined}
                              data-bs-target={canProduce ? "#modal-form-production" : undefined}
                              onClick={() => canProduce && onOpenProduction(group, variant)}>
                              Produksi
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="product-group-detail-footer">
          <p className="text-secondary mb-0">
            Untuk stok siap jual, atur resep lalu Produksi. Bahan tidak dikurangi lagi saat produk dijual.
          </p>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Selesai
          </button>
        </footer>
      </section>
    </div>
  );
};

export default DetailProductGroup;
